import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User, FileWarning, ClipboardCheck, Clock,
  CheckCircle2, AlertCircle,
} from 'lucide-react'
import { useAuth } from '../auth'
import { issueApi } from '../services/issueApi'
import { checklistApi } from '../services/checklistApi'
import { projectApi } from '../services/projectApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { EmptyState } from '../components/common/EmptyState'
import { StatusBadge } from '../components/common/StatusBadge'
import { PriorityBadge } from '../components/common/PriorityBadge'
import { ProgressBar } from '../components/common/ProgressBar'
import type { Issue, Checklist, Project } from '../types'

type TabKey = 'issues' | 'checklists'

function isAssignedToUser(
  assignedTo: string | null | undefined,
  assignedToName: string | null | undefined,
  userName: string,
  userEmail: string,
): boolean {
  const lower = (s: string | null | undefined) => (s || '').toLowerCase().trim()
  const uName = lower(userName)
  const uEmail = lower(userEmail)
  if (!uName && !uEmail) return false
  const aTo = lower(assignedTo)
  const aName = lower(assignedToName)
  return (
    (!!aTo && (aTo === uName || aTo === uEmail)) ||
    (!!aName && (aName === uName || aName === uEmail))
  )
}

function isOverdue(dueDate: string | null | undefined, status: string): boolean {
  if (!dueDate) return false
  const closedStatuses = ['Closed', 'Resolved', 'Complete']
  if (closedStatuses.includes(status)) return false
  return dueDate < new Date().toISOString().split('T')[0]!
}

function isOpenStatus(status: string): boolean {
  const closed = ['Closed', 'Resolved', 'Complete']
  return !closed.includes(status)
}

export function MyAssignments() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>('issues')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [allIssues, setAllIssues] = useState<Issue[]>([])
  const [allChecklists, setAllChecklists] = useState<Checklist[]>([])
  const [projects, setProjects] = useState<Project[]>([])

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)
        setError(null)

        const [issuesResult, projectsResult] = await Promise.all([
          issueApi.getAll(),
          projectApi.getAll({ pageSize: 1000 }),
        ])

        setAllIssues(issuesResult)
        setProjects(projectsResult.items)

        // Fetch checklists per project (no global getAll endpoint)
        const checklistResults = await Promise.all(
          projectsResult.items.map((p) => checklistApi.getByProject(String(p.id)).catch(() => [] as Checklist[]))
        )
        setAllChecklists(checklistResults.flat())
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load assignments')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const userName = user?.name || ''
  const userEmail = user?.email || ''

  const myIssues = useMemo(
    () => allIssues.filter((item) => isAssignedToUser(item.assignedTo, item.assignedToName, userName, userEmail)),
    [allIssues, userName, userEmail],
  )

  const myChecklists = useMemo(
    () => allChecklists.filter((item) => isAssignedToUser(item.assignedTo, null, userName, userEmail)),
    [allChecklists, userName, userEmail],
  )

  const totalCount = myIssues.length + myChecklists.length
  const openCount = [
    ...myIssues.filter((i) => isOpenStatus(i.status)),
    ...myChecklists.filter((i) => isOpenStatus(i.status)),
  ].length
  const overdueCount = myIssues.filter((i) => isOverdue(i.dueDate, i.status)).length

  function projectName(projectId: number | string): string {
    const p = projects.find((proj) => String(proj.id) === String(projectId))
    return p?.name || `#${projectId}`
  }

  const tabs: { key: TabKey; label: string; icon: typeof FileWarning; count: number }[] = [
    { key: 'issues', label: 'Issues', icon: FileWarning, count: myIssues.length },
    { key: 'checklists', label: 'Checklists', icon: ClipboardCheck, count: myChecklists.length },
  ]

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="d-flex align-center gap-3">
            <User size={28} />
            My Work
          </h1>
          <p className="page-subtitle">
            Items assigned to {user?.name || 'you'} across all projects
          </p>
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      {/* Summary cards */}
      <div className="metrics-grid mb-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--spacing-4)' }}>
        <div className="metric-card">
          <div className="metric-label">Total Assigned</div>
          <div className="metric-value">{loading ? '-' : totalCount}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">
            <span className="d-flex align-center gap-2"><CheckCircle2 size={14} color="var(--accent-blue)" /> Open / Active</span>
          </div>
          <div className="metric-value" style={{ color: 'var(--accent-blue)' }}>{loading ? '-' : openCount}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">
            <span className="d-flex align-center gap-2"><AlertCircle size={14} color="var(--accent-red)" /> Overdue</span>
          </div>
          <div className="metric-value" style={{ color: overdueCount > 0 ? 'var(--accent-red)' : undefined }}>{loading ? '-' : overdueCount}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <tab.icon size={16} />
            {tab.label}
            <span className="badge badge-gray-light" style={{ marginLeft: '6px' }}>
              {loading ? '...' : tab.count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner message="Loading your assignments..." />
      ) : (
        <>
          {/* Issues Tab */}
          {activeTab === 'issues' && (
            myIssues.length === 0 ? (
              <EmptyState
                icon={<FileWarning size={48} />}
                title="No issues assigned"
                message="You have no issues assigned to you."
              />
            ) : (
              <div className="table-container">
                <div className="table-summary">{myIssues.length} issue{myIssues.length !== 1 ? 's' : ''}</div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Number</th>
                      <th>Title</th>
                      <th>Project</th>
                      <th>Type</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myIssues.map((item) => {
                      const overdue = isOverdue(item.dueDate, item.status)
                      return (
                        <tr
                          key={item.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/issues?project=${item.projectId}`)}
                        >
                          <td className="font-medium" style={{ color: 'var(--accent-blue)' }}>{item.issueNumber}</td>
                          <td className="font-medium">{item.title}</td>
                          <td className="text-secondary-color">{projectName(item.projectId)}</td>
                          <td><span className="badge badge-gray-light">{item.type}</span></td>
                          <td><PriorityBadge priority={item.priority} /></td>
                          <td><StatusBadge status={item.status} /></td>
                          <td>
                            {item.dueDate ? (
                              <span style={overdue ? { color: 'var(--accent-red)', fontWeight: 600 } : { color: 'var(--text-secondary)' }}>
                                {overdue && <Clock size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />}
                                {new Date(item.dueDate).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-secondary-color">-</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Checklists Tab */}
          {activeTab === 'checklists' && (
            myChecklists.length === 0 ? (
              <EmptyState
                icon={<ClipboardCheck size={48} />}
                title="No checklists assigned"
                message="You have no checklists assigned to you."
              />
            ) : (
              <div className="table-container">
                <div className="table-summary">{myChecklists.length} checklist{myChecklists.length !== 1 ? 's' : ''}</div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Template</th>
                      <th>Project</th>
                      <th>Status</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myChecklists.map((item) => (
                      <tr
                        key={item.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/checklists/${item.id}`)}
                      >
                        <td className="font-medium">{item.name}</td>
                        <td className="text-secondary-color">{item.templateName}</td>
                        <td className="text-secondary-color">{projectName(item.projectId)}</td>
                        <td><StatusBadge status={item.status} /></td>
                        <td style={{ minWidth: 140 }}>
                          <ProgressBar value={item.completionPercentage} size="sm" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}
    </div>
  )
}
